import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Card } from './surveyBaseTheme.styles';

// Shared styled-components for the Survey feature (instructor, student, and
// public magic-link pages) — mirrors the visual language already used by the
// quiz feature (src/pages/instructor/quizzes/*.styles.js): gold-tinted
// gradient buttons, 12-24px card radii, soft warm shadows, tinted status
// pills. There's no app-wide shared Button/Card component, so each feature
// rolls its own — this is the survey feature's single copy, reused by every
// survey page instead of duplicating it per-file.

export const PageContainer = styled.div`
  width: 100%;
  max-width: ${props => props.$narrow ? '860px' : '1100px'};
  margin: 0 auto;
  padding: 2rem 1rem 3rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 768px) {
    padding: 1rem 0.75rem 2rem;
  }
`;

export const HeroRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.875rem;
    margin-bottom: 1.125rem;
  }
`;

export const HeroEyebrow = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
`;

export const HeroTitle = styled.h1`
  position: relative;
  /* $large matches the My Assignments / My Quizzes & Exams page-header size
     (Assignments.module.css / StudentQuizList.module.css .heroTitle) — used
     by list/landing pages (StudentSurveyList). $compact is smaller still,
     for the question-answering surfaces (SurveyPlayer, PublicSurveyPlayer,
     the instructor SurveyPreview) where the survey's own title shouldn't
     compete with the question card below it. Everywhere else (the survey
     builder, results) stays at the plain default. */
  font-size: ${props => props.$large
    ? 'clamp(1.75rem, 2.6vw + 1rem, 2.5rem)'
    : props.$compact
      ? 'clamp(1rem, 1.3vw + 0.75rem, 1.3rem)'
      : 'clamp(1.2rem, 1.6vw + 0.9rem, 1.6rem)'};
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin: 0 0 0.625rem 0;
  padding-bottom: 0.5rem;
  background: linear-gradient(135deg, #1a202c 0%, #1a202c 55%, #6b4f25 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;

  &::after {
    /* Hairline gold accent under the title, matching the quiz/assignments hero. */
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 2.25rem;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(90deg, #B8956A 0%, rgba(184, 149, 106, 0) 100%);
  }
`;

export const HeroSubtitle = styled.p`
  color: var(--text-muted);
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.55;
`;

const buttonBase = `
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.15rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  cursor: pointer;
  white-space: nowrap;
  min-height: 40px;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  border: none;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  color: var(--text-inverse);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(184, 149, 106, 0.35);
  }
`;

export const GhostButton = styled.button`
  ${buttonBase}
  border: 1px solid rgba(216, 184, 137, 0.3);
  background: rgba(255, 255, 255, 0.82);
  color: var(--text-primary);
  text-decoration: none;

  &:hover:not(:disabled) {
    background: var(--background-primary);
    border-color: var(--primary-color);
    transform: translateY(-1px);
  }

  body.dark-mode & {
    background: var(--background-tertiary);
  }
`;

export const DangerButton = styled(GhostButton)`
  border-color: rgba(229, 62, 62, 0.35);
  color: var(--error-color);

  &:hover:not(:disabled) {
    background: rgba(229, 62, 62, 0.06);
    border-color: var(--error-color);
  }
`;

const pillTones = {
  draft: 'background: linear-gradient(135deg, rgba(246, 224, 94, 0.14) 0%, rgba(214, 158, 46, 0.06) 100%); color: var(--warning-dark, #B7791F);',
  published: 'background: #EAF3DE; color: #3B6D11;',
  closed: 'background: rgba(113, 128, 150, 0.14); color: var(--text-muted);',
  anonymous: 'background: rgba(49, 130, 206, 0.12); color: var(--info-color, #3182CE);',
  completed: 'background: rgba(56, 161, 105, 0.14); color: var(--success-color, #38A169);',
  pending: 'background: linear-gradient(135deg, rgba(216, 184, 137, 0.15) 0%, rgba(216, 184, 137, 0.08) 100%); color: var(--primary-color);',
  overdue: 'background: rgba(229, 62, 62, 0.12); color: var(--error-color, #E53E3E);',
};

export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  ${props => pillTones[props.$tone] || pillTones.pending}
`;

export { Card };

export const ListCard = styled.div`
  background: #fff;
  border: 0.5px solid #d9d9d9;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 1rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  ${props => props.$onClick ? 'cursor: pointer;' : ''}

  &:hover {
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
    transform: translateY(-1px);
  }

  body.dark-mode & {
    background: var(--background-primary);
    border-color: var(--border-color);
  }

  @media (max-width: 768px) {
    border-radius: 14px;
    padding: 16px;
  }
`;

export const EmptyState = styled(Card)`
  text-align: center;
  color: var(--text-muted);
`;

// Form fields, option rows, and a few smaller pieces live in
// surveyFieldTheme.styles.js (kept out of this file to stay under the lint's
// max-lines limit) — re-exported here so every consumer can keep importing
// from this one file regardless of which physical file a piece lives in.
export * from './surveyFieldTheme.styles';

export const BackLink = styled(Link)`
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    color: var(--primary-color);
  }
`;

export const Divider = styled.div`
  border-top: 1px solid rgba(216, 184, 137, 0.22);
  margin: 1rem 0;
`;

export const SectionLabel = styled.h3`
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  margin: 0 0 0.85rem;
`;
